// PageDescription: 매거진 및 자주 묻는 질문 리스트 페이지
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import Head from 'next/head';

import DefaultLayout from '@/components/layouts/DefaultLayout';
import MagazineTemplate from '@/components/templates/communication/Magazine/MagazineTemplate';
import { Urls } from '@/constants/url';
import useAppInstallTopBanner from '@/hooks/Cuj/useAppInstallTopBanner';
import useGetBannerAdExchange from '@/hooks/ad/useGetBannerAdExchange';
import useGetMagazineViewModel from '@/hooks/magazine/useGetMagazineViewModel';
import useGetMagazines from '@/hooks/magazine/useGetMagazines';
import { AdPageType } from '@/models/ad/list';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { MagazineLineItemVo, MagazineType } from '@/types/magazine';
import { withClientServerSideProps } from '@/utils/mobile/RNPlugins';
import dayjs from 'dayjs';

const createNewestMagazineLdJson = (magazines: Array<MagazineLineItemVo>, faqs: Array<MagazineLineItemVo>) => {
  const magazineItems = magazines.map((magazine, i) => ({
    '@type': 'Article',
    '@id': `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.MAGAZINE.DETAIL.path}/${magazine.magazinePid}`,
    position: `${i + 1}`,
    headline: magazine.title,
    name: magazine.title,
    image: magazine.image,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.MAGAZINE.DETAIL.path}/${magazine.magazinePid}`,
    },
    url: `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.MAGAZINE.DETAIL.path}/${magazine.magazinePid}`,
    datePublished: dayjs(magazine.date).format('YYYY-MM-DDTHH:mm:ss[Z]'),
    author: {
      '@type': 'Organization',
      name: '닥터나우',
      url: `${process.env.NEXT_PUBLIC_DOMAIN}`,
    },
  }));

  const faqItems = faqs.map((faq, i) => ({
    '@type': 'Question',
    position: `${magazineItems.length + i + 1}`,
    name: faq.title,
    description: faq.description,
    url: `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.FAQ.DETAIL.path}/${faq.magazinePid}`,
    dateCreated: dayjs(faq.date).format('YYYY-MM-DDTHH:mm:ss[Z]'),
  }));

  const itemListElement = [...magazineItems, ...faqItems];

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: '건강 매거진 - 닥터나우',
    description: '건강에 대한 궁금증을 닥터나우만의 전문 콘텐츠로 해결해 보세요.',
    url: `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.MAGAZINE.HOME.path}`,
    itemListElement,
  };
};

type Props = CustomPageProps;
const HealthMagazinePage = ({ client }: Props) => {
  const viewModel = useGetMagazineViewModel();
  const topBannerViewModel = useAppInstallTopBanner(client);

  const ldJson = createNewestMagazineLdJson(viewModel.magazines, viewModel.faqs);
  return (
    <>
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }} />
      </Head>
      <NextSeo
        title='건강 매거진 - 닥터나우'
        description='건강에 대한 궁금증을 닥터나우만의 전문 콘텐츠로 해결해 보세요.'
        openGraph={{
          title: '건강 매거진 - 닥터나우',
          description: '건강 궁금증을 닥터나우 콘텐츠로 해결해 보세요.',
          url: `${process.env.NEXT_PUBLIC_DOMAIN}${Urls.Magazine}}`,
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_MAGAZINE_OG_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
        canonical='https://doctornow.co.kr/content/magazine'
        languageAlternates={[
          {
            hrefLang: 'ja',
            href: 'https://doctornow.jp/magazine',
          },
          {
            hrefLang: 'ja-JP',
            href: 'https://doctornow.jp/magazine',
          },
          {
            hrefLang: 'ko',
            href: 'https://doctornow.co.kr/content/magazine',
          },
          {
            hrefLang: 'ko-KR',
            href: 'https://doctornow.co.kr/content/magazine',
          },
          {
            hrefLang: 'x-default',
            href: 'https://doctornow.co.kr/content/magazine',
          },
        ]}
      />
      <MagazineTemplate client={client} viewModel={viewModel} topBannerViewModel={topBannerViewModel} />
    </>
  );
};

export default HealthMagazinePage;

HealthMagazinePage.Layout = DefaultLayout;
export const getServerSideProps: GetServerSideProps = withClientServerSideProps(async (ctx) => {
  const REQUEST_SIZE = 5;
  const queryClient = new QueryClient();

  await Promise.allSettled([
    useGetBannerAdExchange.prefetchQuery(queryClient, AdPageType.TopCtaBand, ctx.client),
    useGetMagazines.prefetchQuery(queryClient, {
      magazineCursorPageRequestInput: {
        size: REQUEST_SIZE,
      },
    }),
    useGetMagazines.prefetchQuery(queryClient, {
      magazineCursorPageRequestInput: {
        size: REQUEST_SIZE,
        contentType: MagazineType.FAQ,
      },
    }),
  ]);

  return {
    props: {
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
    },
  };
});
